import { ThumbsDown, ThumbsUp } from '@phosphor-icons/react'
import { cva } from 'class-variance-authority'
import { motion, useAnimationControls, useReducedMotion } from 'motion/react'
import type { ReactNode } from 'react'

import { cn } from '../lib/utils'

export enum ThumbsRating {
  Up = 'up',
  Down = 'down'
}

// A quick overshoot on activation — the only motion in the control, and the thing
// that makes a rating feel received rather than merely recorded.
const POP_SCALE = [1, 1.18, 1]
// One easing per segment. A single back-out curve across both — the literal reading
// of the design, and what CSS does with one timing function — compounds with the
// keyframes: it overshoots the 1.18 peak to 1.20, pulls it forward to 76ms, and dips
// to 0.979 on the way back. Measured, not assumed.
const POP_TRANSITION = { duration: 0.34, times: [0, 0.38, 1], ease: ['easeOut' as const, 'easeInOut' as const] }

// Named off the hook rather than imported: the concrete type lives in motion-dom,
// which isn't a direct dependency here.
type PopControls = ReturnType<typeof useAnimationControls>

// Resting + hover ink per tone. Kept out of the `tone` variant so a tinted active thumb can opt
// out: its disc must survive hover, and a later `hover:bg-*` can't be relied on to win.
const DEFAULT_INK = 'text-muted-foreground hover:border-border hover:bg-surface-hover hover:text-foreground'
const ON_DARK_INK = 'text-white/50 hover:border-white/15 hover:bg-white/10 hover:text-white/90'

// Joined, not `cn()`-merged: tailwind-merge files bare `outline` (outline-style) under
// outline-width and drops it next to `outline-2`, leaving the browser's own two-tone ring.
const thumbVariants = cva(
  [
    'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-transparent',
    'transition-colors active:scale-[0.92] motion-reduce:active:scale-100',
    // The visible button is 32px but the touch target is 44px: a transparent inset
    // pseudo-element carries the extra 12px. Keeping the two apart is what lets a
    // control that sits on the page forever stay visually weightless without
    // dropping under the 44px floor.
    "before:absolute before:-inset-1.5 before:content-['']",
    // A real outline, not a ring: Tailwind paints a ring's offset band in a solid
    // colour, which would cut a stripe of page background out of whatever surface
    // this sits on. An outline's offset is transparent. It reaches 4px past the
    // button, so the focused thumb lifts above its sibling rather than under it.
    'focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
  ].join(' '),
  {
    variants: {
      tone: {
        default: 'focus-visible:outline-pelorous-500 dark:focus-visible:outline-pelorous-300',
        // Sits on a dark brand surface, where the DS pelorous focus ring measures
        // under the 3:1 SC 1.4.11 wants — hence white.
        'on-dark': 'focus-visible:outline-white/70'
      },
      active: { true: '', false: '' },
      // `tinted` adds a coloured disc under the active thumb; inactive thumbs are unchanged.
      emphasis: { ink: '', tinted: '' },
      direction: { up: '', down: '' }
    },
    compoundVariants: [
      { tone: 'default', emphasis: 'ink', class: DEFAULT_INK },
      { tone: 'default', emphasis: 'tinted', active: false, class: DEFAULT_INK },
      { tone: 'on-dark', emphasis: 'ink', class: ON_DARK_INK },
      { tone: 'on-dark', emphasis: 'tinted', active: false, class: ON_DARK_INK },
      { tone: 'default', emphasis: 'ink', active: true, class: 'text-foreground' },
      { tone: 'on-dark', emphasis: 'ink', active: true, class: 'text-white/85 hover:text-white' },
      // Hover keeps the disc and draws its edge in the ink — the same move as PillButton's `danger`.
      { emphasis: 'tinted', active: true, direction: 'up', class: 'bg-brand-green-tint text-brand-green-on-tint hover:border-brand-green-on-tint' },
      { emphasis: 'tinted', active: true, direction: 'down', class: 'bg-red-wash text-red hover:border-red' }
    ],
    defaultVariants: { tone: 'default', active: false, emphasis: 'ink' }
  }
)

export interface RatingThumbsProps {
  /** Controlled rating; `null` is unrated. */
  value: ThumbsRating | null
  /** Fires with the new rating, or `null` when the user clears by re-activating the active thumb. */
  onChange: (value: ThumbsRating | null) => void
  tone?: 'default' | 'on-dark'
  /**
   * `ink` carries state by icon weight alone. `tinted` also puts the active thumb on a
   * green (up) or red (down) disc — for surfaces where the rating is the primary content.
   */
  emphasis?: 'ink' | 'tinted'
  /**
   * Accessible names for the two buttons — already translated; this package stays
   * i18n-agnostic. Recompute them from `value` at the call site when the action
   * changes meaning (a rated thumbs-down that reopens a form is "Edit your
   * feedback", not "This isn't right").
   */
  upLabel: string
  downLabel: string
  /**
   * Trailing text beside the thumbs — the control's only state readout, so it
   * carries whatever the surface wants to say about the rating. Wraps rather than
   * truncates.
   */
  caption?: ReactNode
  className?: string
}

/**
 * A two-button up/down rating.
 *
 * Deliberately generic: it knows nothing about what is being rated. In the default `ink`
 * emphasis, state is carried almost entirely by **icon weight** — regular when inactive, filled
 * when active — with no chip, fill or border appearing on the rated button. That is what lets it
 * sit permanently under a piece of content without competing with it: a secondary action on
 * someone else's primary content. `tinted` is for the opposite situation — the rating *is* the
 * content (a preference grid) — and adds a coloured disc under the active thumb.
 *
 * The visible button is 32px and the touch target is 44px, on purpose — see the note on
 * `before:-inset-1.5`. Activating the already-active thumb clears the rating; activating the
 * other switches it.
 */
export function RatingThumbs({
  value,
  onChange,
  tone = 'default',
  emphasis = 'ink',
  upLabel,
  downLabel,
  caption,
  className
}: RatingThumbsProps) {
  const upPop = useAnimationControls()
  const downPop = useAnimationControls()
  const reduce = useReducedMotion()

  const select = (next: ThumbsRating, pop: PopControls) => {
    const cleared = value === next
    onChange(cleared ? null : next)
    // Confirms the rating landed. Clearing gets no pop — nothing was affirmed.
    if (!cleared && !reduce) void pop.start({ scale: POP_SCALE }, POP_TRANSITION)
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-0.5', className)}>
      <button
        type="button"
        aria-pressed={value === ThumbsRating.Up}
        aria-label={upLabel}
        onClick={() => select(ThumbsRating.Up, upPop)}
        className={thumbVariants({ tone, emphasis, direction: 'up', active: value === ThumbsRating.Up })}>
        <motion.span animate={upPop} className="inline-flex">
          <ThumbsUp size={20} weight={value === ThumbsRating.Up ? 'fill' : 'regular'} />
        </motion.span>
      </button>
      <button
        type="button"
        aria-pressed={value === ThumbsRating.Down}
        aria-label={downLabel}
        onClick={() => select(ThumbsRating.Down, downPop)}
        className={thumbVariants({ tone, emphasis, direction: 'down', active: value === ThumbsRating.Down })}>
        <motion.span animate={downPop} className="inline-flex">
          <ThumbsDown size={20} weight={value === ThumbsRating.Down ? 'fill' : 'regular'} />
        </motion.span>
      </button>
      {caption ? (
        <span className={cn('ml-2 text-xs font-medium tracking-wide', tone === 'on-dark' ? 'text-white/45' : 'text-muted-foreground')}>
          {caption}
        </span>
      ) : null}
    </div>
  )
}

export { thumbVariants }
